// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction } from "@insite/client-framework/Store/Reducers";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xGoogleAnalyticsUpdate } from "@west/App/Handlers/GoogleAnalytics/xGoogleAnalytics";
import { getGoogleAnalyticsConnectorMapState } from "@west/App/Store/Selectors/GoogleAnalyticsSelectors";
import { XNGAGE_CUSTOM_WIDGET } from "@west/XDL/constants";
import { getDataLayer } from "@west/XDL/Utilities/tracking";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";
import { css } from "styled-components";

const enum fields {
    // Basic Events
    trackGoogleAnalyticsEvents = "trackGoogleAnalyticsEvents",
    checkoutFlowEvents = "checkoutFlowEvents",
    addToCartEvent = "addToCartEvent",
    addToCartEventName = "addToCartEventName",
    purchaseEvent = "purchaseEvent",
    purchaseEventName = "purchaseEventName",
    removeFromCartLineEvent = "removeFromCartLineEvent",
    removeFromCartLineEventName = "removeFromCartLineEventName",
    pdpViewEvent = "pdpViewEvent",
    pdpViewEventName = "pdpViewEventName",
    productImpressionEvent = "productImpressionEvent",
    productImpressionEventName = "productImpressionEventName",
    selectItemEvent = "selectItemEvent",
    selectItemEventName = "selectItemEventName",
    selectPromotionEvent = "selectPromotionEvent",
    selectPromotionEventName = "selectPromotionEventName",
    searchEvent = "searchEvent",
    searchEventName = "searchEventName",
    viewCartEvent = "viewCartEvent",
    viewCartEventName = "viewCartEventName",
    // Settings fields
    includeProductBrand = "includeProductBrand",
    includeProductCategory = "includeProductCategory",
    // Custom Events
    loginEvent = "loginEvent",
    loginEventName = "loginEventName",
    setDasboardAsHomepageEvent = "setDasboardAsHomepageEvent",
    setDasboardAsHomepageEventName = "setDasboardAsHomepageEventName",
    returnRequestEvent = "returnRequestEvent",
    returnRequestEventName = "returnRequestEventName",
    // Ecommerce Events
    refundEvent = "refundEvent",
    refundEventName = "refundEventName",
    addToWishListEvent = "addToWishListEvent",
    addToWishListEventName = "addToWishListEventName",
    beginCheckoutEvent = "beginCheckoutEvent",
    beginCheckoutEventName = "beginCheckoutEventName",
    addShippingInfoEvent = "addShippingInfoEvent",
    addShippingInfoEventName = "addShippingInfoEventName",
    addPaymentInfoEvent = "addPaymentInfoEvent",
    addPaymentInfoEventName = "addPaymentInfoEventName",
    applyPromotionCodeEvent = "applyPromotionCodeEvent",
    applyPromotionCodeEventName = "applyPromotionCodeEventName",
    // Custom West Events
    registrationEvent = "registrationEvent",
    registrationEventName = "registrationEventName",
    contactUsClickEvent = "contactUsClickEvent",
    contactUsClickEventName = "contactUsClickEventName",
    contactUsSubmitEvent = "contactUsSubmitEvent",
    contactUsSubmitEventName = "contactUsSubmitEventName",
    newsletterSubscribeEvent = "newsletterSubscribeEvent",
    newsletterSubscribeEventName = "newsletterSubscribeEventName",
    hpBannerClickEvent = "hpBannerClickEvent",
    hpBannerClickEventName = "hpBannerClickEventName",
    hpTopRightBanner1ClickEvent = "hpTopRightBanner1ClickEvent",
    hpTopRightBanner1ClickEventName = "hpTopRightBanner1ClickEventName",
    hpTopRightBanner2ClickEvent = "hpTopRightBanner2ClickEvent",
    hpTopRightBanner2ClickEventName = "hpTopRightBanner2ClickEventName",
    hpShopByCategoryEvent = "hpShopByCategoryEvent",
    hpShopByCategoryEventName = "hpShopByCategoryEventName",
    hpTopSellersEvent = "hpTopSellersEvent",
    hpTopSellersEventName = "hpTopSellersEventName",
    featuredBrandsEvent = "featuredBrandsEvent",
    featuredBrandsEventName = "featuredBrandsEventName",
    hpFooterWidgetEvent = "hpFooterWidgetEvent",
    hpFooterWidgetEventName = "hpFooterWidgetEventName",
    footerLinksClickEvent = "footerLinksClickEvent",
    footerLinksClickEventName = "footerLinksClickEventName",
    navBarClickEvent = "navBarClickEvent",
    navBarClickEventName = "navBarClickEventName",
    productCategoryClickEvent = "productCategoryClickEvent",
    productCategoryClickEventName = "productCategoryClickEventName",
    headerToolBarEvent = "headerToolBarEvent",
    headerToolBarEventName = "headerToolBarEventName",
    needHelpClickEvent = "needHelpClickEvent",
    needHelpClickEventName = "needHelpClickEventName",
    brandsPageLinkClickEvent = "brandsPageLinkClickEvent",
    brandsPageLinkClickEventName = "brandsPageLinkClickEventName",
    socialButtonsClickEvent = "socialButtonsClickEvent",
    socialButtonsClickEventName = "socialButtonsClickEventName",
    myAccountFlyoutEvent = "myAccountFlyoutEvent",
    myAccountFlyoutEventName = "myAccountFlyoutEventName",
    myAccountNavPageLinksEvent = "myAccountNavPageLinksEvent",
    pdpShareEvent = "pdpShareEvent",
    pdpShareEventName = "pdpShareEventName",
    pdpPrintEvent = "pdpPrintEvent",
    pdpPrintEventName = "pdpPrintEventName",
    pdpDocumentClickEvent = "pdpDocumentClickEvent",
    pdpDocumentClickEventName = "pdpDocumentClickEventName",
    pdpBannerClickEvent = "pdpBannerClickEvent",
    pdpBannerClickEventName = "pdpBannerClickEventName",
    fileDownloadEvent = "fileDownloadEvent",
    fileDownloadEventName = "fileDownloadEventName",
    uploadBulkOrderEvent = "uploadBulkOrderEvent",
    uploadBulkOrderEventName = "uploadBulkOrderEventName",
    quickBulkOrderEvent = "quickBulkOrderEvent",
    quickBulkOrderEventName = "quickBulkOrderEventName",
    updateZipCodeEvent = "updateZipCodeEvent",
    updateZipCodeEventName = "updateZipCodeEventName",
    hpRecentOrdersEvent = "hpRecentOrdersEvent",
    hpRecentOrdersEventName = "hpRecentOrdersEventName",
    hpRecentSavedCartsEvent = "hpRecentSavedCartsEvent",
    hpRecentSavedCartsEventName = "hpRecentSavedCartsEventName",
    compareProductsEvent = "compareProductsEvent",
    compareProductsEventName = "compareProductsEventName",
    autoCompleteSearchEvent = "autoCompleteSearchEvent",
    autoCompleteSearchEventName = "autoCompleteSearchEventName",
}

interface OwnProps extends WidgetProps {
    fields: {
        // Basic Events
        [fields.trackGoogleAnalyticsEvents]: boolean;
        [fields.checkoutFlowEvents]: boolean;
        [fields.addToCartEvent]: boolean;
        [fields.addToCartEventName]: string;
        [fields.purchaseEvent]: boolean;
        [fields.purchaseEventName]: string;
        [fields.removeFromCartLineEvent]: boolean;
        [fields.removeFromCartLineEventName]: string;
        [fields.pdpViewEvent]: boolean;
        [fields.pdpViewEventName]: string;
        [fields.productImpressionEvent]: boolean;
        [fields.productImpressionEventName]: string;
        [fields.selectItemEvent]: boolean;
        [fields.selectItemEventName]: string;
        [fields.selectPromotionEvent]: boolean;
        [fields.selectPromotionEventName]: string;
        [fields.searchEvent]: boolean;
        [fields.searchEventName]: string;
        [fields.viewCartEvent]: boolean;
        [fields.viewCartEventName]: string;
        // Settings Fields
        [fields.includeProductBrand]: boolean;
        [fields.includeProductCategory]: boolean;
        // Custom Events
        [fields.loginEvent]: boolean;
        [fields.loginEventName]: string;
        [fields.setDasboardAsHomepageEvent]: boolean;
        [fields.setDasboardAsHomepageEventName]: string;
        [fields.returnRequestEvent]: boolean;
        [fields.returnRequestEventName]: string;
        // Ecommerce Events
        [fields.refundEvent]: boolean;
        [fields.refundEventName]: string;
        [fields.addToWishListEvent]: boolean;
        [fields.addToWishListEventName]: string;
        [fields.beginCheckoutEvent]: boolean;
        [fields.beginCheckoutEventName]: string;
        [fields.addShippingInfoEvent]: boolean;
        [fields.addShippingInfoEventName]: string;
        [fields.addPaymentInfoEvent]: boolean;
        [fields.addPaymentInfoEventName]: string;
        [fields.applyPromotionCodeEvent]: boolean;
        [fields.applyPromotionCodeEventName]: string;
        // Custom West Events
        [fields.registrationEvent]: boolean;
        [fields.registrationEventName]: string;
        [fields.contactUsClickEvent]: boolean;
        [fields.contactUsClickEventName]: string;
        [fields.contactUsSubmitEvent]: boolean;
        [fields.contactUsSubmitEventName]: string;
        [fields.newsletterSubscribeEvent]: boolean;
        [fields.newsletterSubscribeEventName]: string;
        [fields.hpBannerClickEvent]: boolean;
        [fields.hpBannerClickEventName]: string;
        [fields.hpTopRightBanner1ClickEvent]: boolean;
        [fields.hpTopRightBanner1ClickEventName]: string;
        [fields.hpTopRightBanner2ClickEvent]: boolean;
        [fields.hpTopRightBanner2ClickEventName]: string;
        [fields.hpShopByCategoryEvent]: boolean;
        [fields.hpShopByCategoryEventName]: string;
        [fields.hpTopSellersEvent]: boolean;
        [fields.hpTopSellersEventName]: string;
        [fields.featuredBrandsEvent]: boolean;
        [fields.featuredBrandsEventName]: string;
        [fields.hpFooterWidgetEvent]: boolean;
        [fields.hpFooterWidgetEventName]: string;
        [fields.footerLinksClickEvent]: boolean;
        [fields.footerLinksClickEventName]: string;
        [fields.navBarClickEvent]: boolean;
        [fields.navBarClickEventName]: string;
        [fields.productCategoryClickEvent]: boolean;
        [fields.productCategoryClickEventName]: string;
        [fields.headerToolBarEvent]: boolean;
        [fields.headerToolBarEventName]: string;
        [fields.needHelpClickEvent]: boolean;
        [fields.needHelpClickEventName]: string;
        [fields.brandsPageLinkClickEvent]: boolean;
        [fields.brandsPageLinkClickEventName]: string;
        [fields.socialButtonsClickEvent]: boolean;
        [fields.socialButtonsClickEventName]: string;
        [fields.myAccountFlyoutEvent]: boolean;
        [fields.myAccountFlyoutEventName]: string;
        [fields.myAccountNavPageLinksEvent]: boolean;
        [fields.myAccountFlyoutEventName]: string;
        [fields.pdpShareEvent]: boolean;
        [fields.pdpShareEventName]: string;
        [fields.pdpPrintEvent]: boolean;
        [fields.pdpPrintEventName]: string;
        [fields.pdpDocumentClickEvent]: boolean;
        [fields.pdpDocumentClickEventName]: string;
        [fields.pdpBannerClickEvent]: boolean;
        [fields.pdpBannerClickEventName]: string;
        [fields.fileDownloadEvent]: boolean;
        [fields.fileDownloadEventName]: string;
        [fields.uploadBulkOrderEvent]: boolean;
        [fields.uploadBulkOrderEventName]: string;
        [fields.quickBulkOrderEvent]: boolean;
        [fields.quickBulkOrderEventName]: string;
        [fields.updateZipCodeEvent]: boolean;
        [fields.updateZipCodeEventName]: string;
        [fields.hpRecentOrdersEvent]: boolean;
        [fields.hpRecentOrdersEventName]: string;
        [fields.hpRecentSavedCartsEvent]: boolean;
        [fields.hpRecentSavedCartsEventName]: string;
        [fields.compareProductsEvent]: boolean;
        [fields.compareProductsEventName]: string;
        [fields.autoCompleteSearchEvent]: boolean;
        [fields.autoCompleteSearchEventName]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        ...getGoogleAnalyticsConnectorMapState(state),
    };
};

const mapDispatchToProps = {
    xGoogleAnalyticsUpdate,
    resetShouldTrackPayment: () => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "xGoogleAnalytics/setShouldTrackPayment",
            shouldTrackPayment: false,
        } as any);
    },
    resetShouldTrackBeginCheckout: () => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "xGoogleAnalytics/setShouldTrackBeginOnePageCheckout",
            shouldTrackBeginOnePageCheckout: false,
        } as any);
    },
    setAfilliation: (affiliation?: string) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "xGoogleAnalytics/setAffiliation",
            affiliation,
        } as any);
    },
    setAuthenticationState: (authenticationState?: string) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "xGoogleAnalytics/setAuthenticationState",
            authenticationState,
        } as any);
    },
    setCurrentPage: (currentPage?: string) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "xGoogleAnalytics/setCurrentPage",
            currentPage,
        } as any);
    },
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface GoogleAnalyticsConnectorStyles {
    container?: InjectableCss;
}

const googleAnalyticsConnectorStyles: GoogleAnalyticsConnectorStyles = {
    container: {
        css: css`
            background: #e5e5e5;
            padding: 10px;
        `,
    },
};

const styles = googleAnalyticsConnectorStyles;
const GoogleAnalyticsConnector: FC<Props> = ({
    xGoogleAnalyticsUpdate,
    fields,
    cart,
    isCartPage,
    xGoogleAnalytics,
    currentPage,
    currencyCode,
    affiliation,
    locationId,
    isPLP,
    isPDP,
    PLPProducts,
    PDPProduct,
    resetShouldTrackPayment,
    resetShouldTrackBeginCheckout,
    userEmail,
    query,
    setAfilliation,
    // West Customizations
    authenticationState,
    isContactUsPage,
    setAuthenticationState,
    compareProductsIds,
    isComparePage,
    setCurrentPage,
}) => {
    const { isInShell } = useShellContext();

    const [PDPInfoLoaded, setPDPInfoLoaded] = useState<boolean>(false);
    const [PLPInfoLoaded, setPLPInfoLoaded] = useState<boolean>(false);
    // Placeholder in CMS view
    if (isInShell) {
        return (
            <StyledWrapper {...styles.container} data-test-selectore="GoogleAnalyticsConnector">
                <Typography>{translate("Google Analytics Connector Widget")}</Typography>
            </StyledWrapper>
        );
    }

    useEffect(() => {
        if (!fields.trackGoogleAnalyticsEvents) {
            return;
        }

        xGoogleAnalyticsUpdate({
            trackGoogleAnalytics: true,
            checkoutFlowEvents: fields["checkoutFlowEvents"] || false,
            addToCartEvent: fields["addToCartEvent"] || false,
            addToCartEventName: fields["addToCartEventName"] || "add_to_cart",
            purchaseEvent: fields["purchaseEvent"] || false,
            purchaseEventName: fields["purchaseEventName"] || "purchase",
            removeFromCartLineEvent: fields["removeFromCartLineEvent"] || false,
            removeFromCartLineEventName: fields["removeFromCartLineEventName"] || "remove_from_cart",
            pdpViewEvent: fields["pdpViewEvent"] || false,
            pdpViewEventName: fields["pdpViewEventName"] || "view_item",
            productImpressionEvent: fields["productImpressionEvent"] || false,
            productImpressionEventName: fields["productImpressionEventName"] || "view_item_list",
            selectItemEvent: fields["selectItemEvent"] || false,
            selectItemEventName: fields["selectItemEventName"] || "select_item",
            selectPromotionEvent: fields["selectPromotionEvent"] || false,
            selectPromotionEventName: fields["selectPromotionEventName"] || "selectPromotionEventName",
            searchEvent: fields["searchEvent"] || false,
            searchEventName: fields["searchEventName"] || "search",
            viewCartEvent: fields["viewCartEvent"] || false,
            viewCartEventName: fields["viewCartEventName"] || "view_cart",
            includeProductBrand: fields["includeProductBrand"] || false,
            includeProductCategory: fields["includeProductCategory"] || false,
            loginEvent: fields["loginEvent"] || false,
            loginEventName: fields["loginEventName"] || "login",
            setDasboardAsHomepageEvent: fields["setDasboardAsHomepageEvent"] || false,
            setDasboardAsHomepageEventName: fields["setDasboardAsHomepageEventName"] || "make_dash_hmpg",
            returnRequestEvent: fields["returnRequestEvent"] || false,
            returnRequestEventName: fields["returnRequestEventName"] || "return_request",
            // Ecommerce Events
            refundEvent: fields["refundEvent"] || false,
            refundEventName: fields["refundEventName"] || "refund",
            addToWishListEvent: fields["addToWishListEvent"] || false,
            addToWishListEventName: fields["addToWishListEventName"] || "add_to_wishlist",
            beginCheckoutEvent: fields["beginCheckoutEvent"] || false,
            beginCheckoutEventName: fields["beginCheckoutEventName"] || "begin_checkout",
            addShippingInfoEvent: fields["addShippingInfoEvent"] || false,
            addShippingInfoEventName: fields["addShippingInfoEventName"] || "add_shipping_info",
            addPaymentInfoEvent: fields["addPaymentInfoEvent"] || false,
            addPaymentInfoEventName: fields["addPaymentInfoEventName"] || "add_payment_info",
            applyPromotionCodeEvent: fields["applyPromotionCodeEvent"] || false,
            applyPromotionCodeEventName: fields["applyPromotionCodeEventName"] || "promo_code",
            // Custom West Events
            registrationEvent: fields["registrationEvent"] || false,
            registrationEventName: fields["registrationEventName"] || "registration",
            contactUsClickEvent: fields["contactUsClickEvent"] || false,
            contactUsClickEventName: fields["contactUsClickEventName"] || "contactus_click",
            contactUsSubmitEvent: fields["contactUsSubmitEvent"] || false,
            contactUsSubmitEventName: fields["contactUsSubmitEventName"] || "contactus_submit",
            newsletterSubscribeEvent: fields["newsletterSubscribeEvent"] || false,
            newsletterSubscribeEventName: fields["newsletterSubscribeEventName"] || "newsletter_subscribe",
            hpBannerClickEvent: fields["hpBannerClickEvent"] || false,
            hpBannerClickEventName: fields["hpBannerClickEventName"] || "hp_banner_click",
            hpTopRightBanner1ClickEvent: fields["hpTopRightBanner1ClickEvent"] || false,
            hpTopRightBanner1ClickEventName: fields["hpTopRightBanner1ClickEventName"] || "hp_topright_banner_1",
            hpTopRightBanner2ClickEvent: fields["hpTopRightBanner2ClickEvent"] || false,
            hpTopRightBanner2ClickEventName: fields["hpTopRightBanner2ClickEventName"] || "hp_topright_banner_2",
            hpShopByCategoryEvent: fields["hpShopByCategoryEvent"] || false,
            hpShopByCategoryEventName: fields["hpShopByCategoryEventName"] || "hp_shop_by_category",
            hpTopSellersEvent: fields["hpTopSellersEvent"] || false,
            hpTopSellersEventName: fields["hpTopSellersEventName"] || "hp_top_sellers_sale",
            featuredBrandsEvent: fields["featuredBrandsEvent"] || false,
            featuredBrandsEventName: fields["featuredBrandsEventName"] || "featured_brands",
            hpFooterWidgetEvent: fields["hpFooterWidgetEvent"] || false,
            hpFooterWidgetEventName: fields["hpFooterWidgetEventName"] || "hp_footer_widget",
            footerLinksClickEvent: fields["footerLinksClickEvent"] || false,
            footerLinksClickEventName: fields["footerLinksClickEventName"] || "footer_clicks",
            navBarClickEvent: fields["navBarClickEvent"] || false,
            navBarClickEventName: fields["navBarClickEventName"] || "nav_bar",
            productCategoryClickEvent: fields["productCategoryClickEvent"] || false,
            productCategoryClickEventName: fields["productCategoryClickEventName"] || "product_category_click",
            headerToolBarEvent: fields["headerToolBarEvent"] || false,
            headerToolBarEventName: fields["headerToolBarEventName"] || "tool_bar",
            needHelpClickEvent: fields["needHelpClickEvent"] || false,
            needHelpClickEventName: fields["needHelpClickEventName"] || "need_help_flyout",
            brandsPageLinkClickEvent: fields["brandsPageLinkClickEvent"] || false,
            brandsPageLinkClickEventName: fields["brandsPageLinkClickEventName"] || "brands_page_links",
            socialButtonsClickEvent: fields["socialButtonsClickEvent"] || false,
            socialButtonsClickEventName: fields["socialButtonsClickEventName"] || "social_button_click",
            myAccountFlyoutEvent: fields["myAccountFlyoutEvent"] || false,
            myAccountFlyoutEventName: fields["myAccountFlyoutEventName"] || "myacct_flyout",
            myAccountNavPageLinksEvent: fields["myAccountNavPageLinksEvent"] || false,
            pdpShareEvent: fields["pdpShareEvent"] || false,
            pdpShareEventName: fields["pdpShareEventName"] || "myacct_personal_settings",
            pdpPrintEvent: fields["pdpPrintEvent"] || false,
            pdpPrintEventName: fields["pdpPrintEventName"] || "myacct_addresses",
            pdpDocumentClickEvent: fields["pdpDocumentClickEvent"] || false,
            pdpDocumentClickEventName: fields["pdpDocumentClickEventName"] || "pdp_doc_click",
            pdpBannerClickEvent: fields["pdpBannerClickEvent"] || false,
            pdpBannerClickEventName: fields["pdpBannerClickEventName"] || "pdp_banner_click",
            fileDownloadEvent: fields["fileDownloadEvent"] || false,
            fileDownloadEventName: fields["fileDownloadEventName"] || "file_download",
            uploadBulkOrderEvent: fields["uploadBulkOrderEvent"] || false,
            uploadBulkOrderEventName: fields["uploadBulkOrderEventName"] || "upload_bulk_order",
            quickBulkOrderEvent: fields["quickBulkOrderEvent"] || false,
            quickBulkOrderEventName: fields["quickBulkOrderEventName"] || "quick_bulk_order",
            updateZipCodeEvent: fields["updateZipCodeEvent"] || false,
            updateZipCodeEventName: fields["updateZipCodeEventName"] || "update_zip_code",
            hpRecentOrdersEvent: fields["hpRecentOrdersEvent"] || false,
            hpRecentOrdersEventName: fields["hpRecentOrdersEventName"] || "hp_recent_orders_click",
            hpRecentSavedCartsEvent: fields["hpRecentSavedCartsEvent"] || false,
            hpRecentSavedCartsEventName: fields["hpRecentSavedCartsEventName"] || "hp_recent_saved_carts",
            compareProductsEvent: fields["compareProductsEvent"] || false,
            compareProductsEventName: fields["compareProductsEventName"] || "compare_products",
            autoCompleteSearchEvent: fields["autoCompleteSearchEvent"] || false,
            autoCompleteSearchEventName: fields["autoCompleteSearchEventName"] || "autocomplete_search",
        });
    }, [fields]);

    useEffect(() => {
        const dataLayer = getDataLayer();

        if (!dataLayer) {
            return;
        }

        setCurrentPage(currentPage);
    }, [currentPage]);

    useEffect(() => {
        const dataLayer = getDataLayer();

        if (!dataLayer) {
            return;
        }

        if (isContactUsPage && xGoogleAnalytics?.contactUsClickEvent) {
            const googleAnalyticsData = {
                event: xGoogleAnalytics?.contactUsClickEventName,
                authentication_state: authenticationState,
            };

            dataLayer.push(googleAnalyticsData);
        }
    }, [isContactUsPage, xGoogleAnalytics?.contactUsClickEvent]);

    useEffect(() => {
        const dataLayer = getDataLayer();

        if (!dataLayer) {
            return;
        }

        if (isComparePage && xGoogleAnalytics?.compareProductsEvent) {
            const googleAnalyticsData = {
                event: xGoogleAnalytics.compareProductsEventName,
                items: compareProductsIds?.map((id, idx) => ({
                    [`item_${idx + 1}`]: id,
                })),
                affiliation,
                authentication_state: authenticationState,
            };

            dataLayer.push(googleAnalyticsData);
        }
    }, [isComparePage, xGoogleAnalytics?.compareProductsEvent]);

    useEffect(() => {
        const dataLayer = getDataLayer();

        if (!cart) {
            return;
        }

        if (isCartPage && xGoogleAnalytics?.viewCartEvent) {
            setPDPInfoLoaded(false);
            setPLPInfoLoaded(false);

            const googleAnalyticsData = {
                event: xGoogleAnalytics?.viewCartEventName,
                ecommerce: {
                    currency: currencyCode,
                    value: cart.orderSubTotal,
                    items: cart?.cartLines?.map((cartItem, idx) => ({
                        item_id: cartItem.erpNumber,
                        item_name: cartItem.productName,
                        affiliation,
                        discount: cartItem.pricing
                            ? cartItem.pricing.unitNetPrice < cartItem.pricing.unitListPrice
                                ? cartItem.pricing.unitListPrice - cartItem.pricing.unitNetPrice
                                : 0
                            : 0,
                        price: cartItem.pricing?.unitNetPrice,
                        quantity: cartItem.qtyOrdered,
                        index: idx,
                        item_brand: xGoogleAnalytics["includeProductBrand"] ? cartItem.brand?.name || "" : undefined,
                        item_category: xGoogleAnalytics["includeProductCategory"]
                            ? cartItem.properties?.["category"] || ""
                            : undefined,
                        location_id: locationId,
                    })),
                },
            };

            dataLayer.push(googleAnalyticsData);
            dataLayer.push({ ecommerce: null });
        }
    }, [isCartPage, cart?.id, xGoogleAnalytics?.viewCartEvent]);

    // While navigating between PDPs setPDPInfoLoaded to false in order to track other PDPs
    useEffect(() => {
        setPDPInfoLoaded(false);
    }, [PDPProduct?.productNumber]);

    useEffect(() => {
        const dataLayer = getDataLayer();
        if (dataLayer) {
            if (isPLP && PLPProducts?.length && !PLPInfoLoaded) {
                setPLPInfoLoaded(true);
                setPDPInfoLoaded(false);
                if (xGoogleAnalytics?.productImpressionEvent) {
                    const googleAnalyticsData = {
                        event: xGoogleAnalytics?.productImpressionEventName,
                        ecommerce: {
                            affiliation,
                            authentication_state: authenticationState,
                            items: PLPProducts.map((product, idx) => ({
                                item_name: product?.productTitle,
                                item_id: product?.productNumber,
                                price: product?.pricing?.unitNetPrice,
                                index: idx,
                                item_brand: xGoogleAnalytics["includeProductBrand"]
                                    ? product?.brand?.name || ""
                                    : undefined,
                                item_category: xGoogleAnalytics["includeProductCategory"]
                                    ? product?.properties?.["category"] || ""
                                    : undefined,
                                item_list_id: xGoogleAnalytics["includeProductCategory"]
                                    ? product?.properties?.["categoryId"] || ""
                                    : undefined,
                            })),
                        },
                    };

                    dataLayer.push(googleAnalyticsData);
                    dataLayer.push({ ecommerce: null });
                }
            }

            if (isPDP && xGoogleAnalytics?.pdpViewEvent && PDPProduct && !PDPInfoLoaded) {
                setPDPInfoLoaded(true);
                setPLPInfoLoaded(false);
                const googleAnalyticsData = {
                    event: xGoogleAnalytics.pdpViewEventName,
                    ecommerce: {
                        currency: currencyCode,
                        value: cart?.orderGrandTotal,
                        affiliation,
                        authentication_state: authenticationState,
                        items: [
                            {
                                item_name: PDPProduct?.productTitle,
                                item_id: PDPProduct?.productNumber,
                                price: PDPProduct.pricing?.unitNetPrice,
                                discount: PDPProduct.pricing
                                    ? PDPProduct.pricing.unitNetPrice < PDPProduct.pricing.unitListPrice
                                        ? PDPProduct.pricing.unitListPrice - PDPProduct.pricing.unitNetPrice
                                        : 0
                                    : 0,
                                index: 0,
                                item_list_name: "Product Detail Page",
                                item_brand: xGoogleAnalytics["includeProductBrand"]
                                    ? PDPProduct.brand?.name || ""
                                    : undefined,
                                item_category: xGoogleAnalytics["includeProductCategory"]
                                    ? PDPProduct.properties?.["category"] || ""
                                    : undefined,
                                item_list_id: xGoogleAnalytics["includeProductCategory"]
                                    ? PDPProduct.properties?.["categoryId"] || ""
                                    : undefined,
                                affiliation,
                                qty: PDPProduct.minimumOrderQty || 1,
                            },
                        ],
                    },
                };
                const isDuplicatedEvent =
                    dataLayer[dataLayer.length - 1]?.event === xGoogleAnalytics?.pdpViewEventName &&
                    dataLayer[dataLayer.length - 1]?.ecommerce.items[0].item_id === PDPProduct?.productNumber;

                const item = hasProductInViewItemList(PDPProduct.productNumber);
                if (item && xGoogleAnalytics.selectItemEvent && PDPProduct && !PDPInfoLoaded) {
                    const googleAnalyticsData = {
                        event: xGoogleAnalytics.selectItemEventName,
                        ecommerce: {
                            item_list_id: xGoogleAnalytics["includeProductCategory"]
                                ? PDPProduct.properties?.["categoryId"] || ""
                                : undefined,
                            item_list_name: "Product Details Page",
                            click_class: "Product Listing Page",
                            affiliation,
                            authentication_state: authenticationState,
                            items: [
                                {
                                    item_name: PDPProduct?.productTitle,
                                    item_id: PDPProduct?.productNumber,
                                    price: PDPProduct.pricing?.unitNetPrice,
                                    discount: PDPProduct.pricing
                                        ? PDPProduct.pricing.unitNetPrice < PDPProduct.pricing.unitListPrice
                                            ? PDPProduct.pricing.unitListPrice - PDPProduct.pricing.unitNetPrice
                                            : 0
                                        : 0,
                                    index: 0,
                                    item_brand: xGoogleAnalytics["includeProductBrand"]
                                        ? PDPProduct.brand?.name || ""
                                        : undefined,
                                    item_category: xGoogleAnalytics["includeProductCategory"]
                                        ? PDPProduct.properties?.["category"] || ""
                                        : undefined,
                                    item_list_id: item.item_list_id,
                                    item_list_name: item.item_list_name,
                                    affiliation,
                                    qty: PDPProduct.minimumOrderQty || 1,
                                },
                            ],
                        },
                    };

                    dataLayer.push(googleAnalyticsData);
                    dataLayer.push({ ecommerce: null });
                }
                if (!isDuplicatedEvent) {
                    dataLayer.push(googleAnalyticsData);
                    dataLayer.push({ ecommerce: null });
                }
            }
        }
    }, [
        PLPProducts?.length,
        PDPProduct?.productNumber,
        currentPage,
        xGoogleAnalytics?.pdpViewEvent,
        xGoogleAnalytics?.productImpressionEvent,
    ]);

    useEffect(() => {
        const dataLayer = getDataLayer();

        if (dataLayer && xGoogleAnalytics?.shouldTrackPayment) {
            (window as any)._GaCheckoutFunnel(
                3,
                cart,
                xGoogleAnalytics.includeProductBrand,
                xGoogleAnalytics.includeProductCategory,
                xGoogleAnalytics.affiliation,
                xGoogleAnalytics.authenticationState,
            );
            resetShouldTrackPayment();
        }
    }, [xGoogleAnalytics?.shouldTrackPayment, cart]);

    useEffect(() => {
        const dataLayer = getDataLayer();
        if (
            dataLayer &&
            xGoogleAnalytics?.checkoutFlowEvents &&
            cart &&
            xGoogleAnalytics?.shouldTrackBeginOnePageCheckout
        ) {
            (window as any)._GaCheckoutFunnel(
                1,
                cart,
                xGoogleAnalytics.includeProductBrand,
                xGoogleAnalytics.includeProductCategory,
                xGoogleAnalytics.affiliation,
                xGoogleAnalytics.authenticationState,
            );
            if (cart.shipVia) {
                (window as any)._GaCheckoutFunnel(
                    2,
                    cart,
                    affiliation,
                    xGoogleAnalytics.includeProductBrand,
                    xGoogleAnalytics.includeProductCategory,
                    xGoogleAnalytics.affiliation,
                    xGoogleAnalytics.authenticationState,
                );
            }
            resetShouldTrackBeginCheckout();
        }
    }, [xGoogleAnalytics?.shouldTrackBeginOnePageCheckout, cart]);

    useEffect(() => {
        setAfilliation(affiliation);
    }, [affiliation]);

    useEffect(() => {
        setAuthenticationState(authenticationState);
    }, [authenticationState]);

    // Function to check for view_item_list events with a specific productNumber
    const hasProductInViewItemList = (productNumber: string) => {
        const dataLayer = getDataLayer();
        if (dataLayer) {
            for (const event of dataLayer) {
                if (event.event === xGoogleAnalytics?.productImpressionEventName && event.ecommerce?.items) {
                    for (const item of event.ecommerce.items) {
                        if (item.item_id === productNumber) {
                            return item;
                        }
                    }
                }
            }
        }
        return false;
    };

    return null;
};

const basicEventsTab = {
    displayName: "Basic Events",
    sortOrder: 1,
};
const customEventsTab = {
    displayName: "Custom Events",
    sortOrder: 2,
};
const settingsTab = {
    displayName: "Settings",
    sortOrder: 3,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(GoogleAnalyticsConnector),
    definition: {
        displayName: "Google Analytics Connector",
        group: XNGAGE_CUSTOM_WIDGET,
        fieldDefinitions: [
            {
                name: fields.trackGoogleAnalyticsEvents,
                displayName: "Track GoogleAnalytics Events",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
            },
            {
                name: fields.addToCartEvent,
                displayName: "Add To Cart (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.addToCartEventName,
                displayName: "Add To Cart (Event Name)",
                editorTemplate: "TextField",
                defaultValue: "add_to_cart",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.addToCartEvent,
            },
            {
                name: fields.removeFromCartLineEvent,
                displayName: "Remove Cartline (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.removeFromCartLineEventName,
                displayName: "Remove Cartline (Event Name)",
                editorTemplate: "TextField",
                defaultValue: "remove_from_cart",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.removeFromCartLineEvent,
            },
            {
                name: fields.pdpViewEvent,
                displayName: "PDP View (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.pdpViewEventName,
                displayName: "PDP View (Event Name)",
                editorTemplate: "TextField",
                defaultValue: "view_item",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.pdpViewEvent,
            },
            {
                name: fields.productImpressionEvent,
                displayName: "Product Impression (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.productImpressionEventName,
                displayName: "Product Impression (Event Name)",
                editorTemplate: "TextField",
                defaultValue: "view_item_list",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.productImpressionEvent,
            },
            {
                name: fields.selectItemEvent,
                displayName: "Select Item (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.selectItemEventName,
                displayName: "Select Item (Event Name)",
                editorTemplate: "TextField",
                defaultValue: "select_item",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.selectItemEvent,
            },
            {
                name: fields.viewCartEvent,
                displayName: "View Cart (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.viewCartEventName,
                displayName: "View Cart (Event Name)",
                editorTemplate: "TextField",
                defaultValue: "view_cart",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.viewCartEvent,
            },
            {
                name: fields.purchaseEvent,
                displayName: "Purchase (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.purchaseEventName,
                displayName: "Purchase (Event Name)",
                editorTemplate: "TextField",
                defaultValue: "purchase",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.purchaseEvent,
            },
            {
                name: fields.checkoutFlowEvents,
                displayName: "Checkout Flow (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.selectPromotionEvent,
                displayName: "Select Promotion Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.selectPromotionEventName,
                displayName: "Select Promotion Event Name",
                editorTemplate: "TextField",
                defaultValue: "select-promotion",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.selectPromotionEvent,
            },
            {
                name: fields.searchEvent,
                displayName: "Search Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.searchEventName,
                displayName: "Search Event Name",
                editorTemplate: "TextField",
                defaultValue: "search",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.searchEvent,
            },
            {
                name: fields.loginEvent,
                displayName: "Login Event",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.loginEventName,
                displayName: "Login Event Name",
                editorTemplate: "TextField",
                defaultValue: "login",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.loginEvent,
            },
            {
                name: fields.setDasboardAsHomepageEvent,
                displayName: "Make Dashboard My Homepage Event",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.setDasboardAsHomepageEventName,
                displayName: "Make Dashboard My Homepage Event Name",
                editorTemplate: "TextField",
                defaultValue: "make_dash_hmpg",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.setDasboardAsHomepageEvent,
            },
            {
                name: fields.returnRequestEvent,
                displayName: "Return Request Event",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.returnRequestEventName,
                displayName: "Return Request Event Name",
                editorTemplate: "TextField",
                defaultValue: "return_request",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.returnRequestEvent,
            },
            {
                name: fields.includeProductBrand,
                displayName: "Include Product Brand (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeProductCategory,
                displayName: "Include Product Category (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            // Ecommerce Events
            {
                name: fields.refundEvent,
                displayName: "Refund Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.refundEventName,
                displayName: "Refund Event Name",
                editorTemplate: "TextField",
                defaultValue: "refund",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.refundEvent,
            },
            {
                name: fields.addToWishListEvent,
                displayName: "Add to Wishlist Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.addToWishListEventName,
                displayName: "Add to Wishlist Event Name",
                editorTemplate: "TextField",
                defaultValue: "add_to_wishlist",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.addToWishListEvent,
            },
            {
                name: fields.beginCheckoutEvent,
                displayName: "Begin Checkout Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.beginCheckoutEventName,
                displayName: "Begin Checkout Event Name",
                editorTemplate: "TextField",
                defaultValue: "begin_checkout",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.beginCheckoutEvent,
            },
            {
                name: fields.addShippingInfoEvent,
                displayName: "Add Shipping Info Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.addShippingInfoEventName,
                displayName: "Add Shipping Info Event Name",
                editorTemplate: "TextField",
                defaultValue: "add_shipping_info",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.addShippingInfoEvent,
            },
            {
                name: fields.addPaymentInfoEvent,
                displayName: "Add Payment Info Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.addPaymentInfoEventName,
                displayName: "Add Payment Info Event Name",
                editorTemplate: "TextField",
                defaultValue: "add_payment_info",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.addPaymentInfoEvent,
            },
            {
                name: fields.applyPromotionCodeEvent,
                displayName: "Apply Promotion Code Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.applyPromotionCodeEventName,
                displayName: "Apply Promotion Code Event Name",
                editorTemplate: "TextField",
                defaultValue: "view_promotion",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.applyPromotionCodeEvent,
            },
            // Custom West Events
            {
                name: fields.registrationEvent,
                displayName: "Registration Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.registrationEventName,
                displayName: "Registration Event Name",
                editorTemplate: "TextField",
                defaultValue: "registration",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.registrationEvent,
            },
            {
                name: fields.contactUsClickEvent,
                displayName: "Contact Us Click Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.contactUsClickEventName,
                displayName: "Contact Us Click Event Name",
                editorTemplate: "TextField",
                defaultValue: "contactus_click",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.contactUsClickEvent,
            },
            {
                name: fields.contactUsSubmitEvent,
                displayName: "Contact Us Submit Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.contactUsSubmitEventName,
                displayName: "Contact Us Submit Event Name",
                editorTemplate: "TextField",
                defaultValue: "contactus_submit",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.contactUsSubmitEvent,
            },
            {
                name: fields.newsletterSubscribeEvent,
                displayName: "Newsletter Subscribe Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.newsletterSubscribeEventName,
                displayName: "Newsletter Subscribe Event Name",
                editorTemplate: "TextField",
                defaultValue: "newsletter_subscribe",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.newsletterSubscribeEvent,
            },
            {
                name: fields.hpBannerClickEvent,
                displayName: "HP Banner Click Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.hpBannerClickEventName,
                displayName: "HP Banner Click Event Name",
                editorTemplate: "TextField",
                defaultValue: "hp_banner_click",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.hpBannerClickEvent,
            },
            {
                name: fields.hpTopRightBanner1ClickEvent,
                displayName: "HP Top Right Banner 1 Click Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.hpTopRightBanner1ClickEventName,
                displayName: "HP Top Right Banner 1 Click Event Name",
                editorTemplate: "TextField",
                defaultValue: "hp_topright_banner_1",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.hpTopRightBanner1ClickEvent,
            },
            {
                name: fields.hpTopRightBanner2ClickEvent,
                displayName: "HP Top Right Banner 2 Click Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.hpTopRightBanner2ClickEventName,
                displayName: "HP Top Right Banner 2 Click Event Name",
                editorTemplate: "TextField",
                defaultValue: "hp_topright_banner_2",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.hpTopRightBanner2ClickEvent,
            },
            {
                name: fields.hpShopByCategoryEvent,
                displayName: "HP Shop by Category Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.hpShopByCategoryEventName,
                displayName: "HP Shop by Category Event Name",
                editorTemplate: "TextField",
                defaultValue: "hp_shop_by_category",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.hpShopByCategoryEvent,
            },
            {
                name: fields.hpTopSellersEvent,
                displayName: "HP Top Sellers Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.hpTopSellersEventName,
                displayName: "HP Top Sellers Event Name",
                editorTemplate: "TextField",
                defaultValue: "hp_top_sellers_sale",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.hpTopSellersEvent,
            },
            {
                name: fields.featuredBrandsEvent,
                displayName: "Featured Brands Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.featuredBrandsEventName,
                displayName: "Featured Brands Event Name",
                editorTemplate: "TextField",
                defaultValue: "featured_brands",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.featuredBrandsEvent,
            },
            {
                name: fields.hpFooterWidgetEvent,
                displayName: "HP Footer Widget Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.hpFooterWidgetEventName,
                displayName: "HP Footer Widget Event Name",
                editorTemplate: "TextField",
                defaultValue: "hp_footer_widget",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.hpFooterWidgetEvent,
            },
            {
                name: fields.footerLinksClickEvent,
                displayName: "Footer Links Click Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.footerLinksClickEventName,
                displayName: "Footer Links Click Event Name",
                editorTemplate: "TextField",
                defaultValue: "footer_clicks",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.footerLinksClickEvent,
            },
            {
                name: fields.navBarClickEvent,
                displayName: "Nav Bar Click Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.navBarClickEventName,
                displayName: "Nav Bar Click Event Name",
                editorTemplate: "TextField",
                defaultValue: "nav_bar",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.navBarClickEvent,
            },
            {
                name: fields.productCategoryClickEvent,
                displayName: "Product Category Click Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.productCategoryClickEventName,
                displayName: "Product Category Click Event Name",
                editorTemplate: "TextField",
                defaultValue: "product_category_click",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.productCategoryClickEvent,
            },
            {
                name: fields.headerToolBarEvent,
                displayName: "Header Tool Bar Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.headerToolBarEventName,
                displayName: "Header Tool Bar Event Name",
                editorTemplate: "TextField",
                defaultValue: "tool_bar",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.headerToolBarEvent,
            },
            {
                name: fields.needHelpClickEvent,
                displayName: "Need Help Click Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.needHelpClickEventName,
                displayName: "Need Help Click Event Name",
                editorTemplate: "TextField",
                defaultValue: "need_help_flyout",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.needHelpClickEvent,
            },
            {
                name: fields.brandsPageLinkClickEvent,
                displayName: "Brands Page Link Click Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.brandsPageLinkClickEventName,
                displayName: "Brands Page Link Click Event Name",
                editorTemplate: "TextField",
                defaultValue: "brands_page_links",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.brandsPageLinkClickEvent,
            },
            {
                name: fields.socialButtonsClickEvent,
                displayName: "Social Buttons Click Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.socialButtonsClickEventName,
                displayName: "Social Buttons Click Event Name",
                editorTemplate: "TextField",
                defaultValue: "social_button_click",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.socialButtonsClickEvent,
            },
            {
                name: fields.myAccountNavPageLinksEvent,
                displayName: "My Account Pages Navigation Links Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.myAccountFlyoutEvent,
                displayName: "My Account Flyout Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.myAccountFlyoutEventName,
                displayName: "My Account Flyout Event Name",
                editorTemplate: "TextField",
                defaultValue: "myacct_flyout",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.myAccountFlyoutEvent,
            },
            {
                name: fields.pdpShareEvent,
                displayName: "PDP Share Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.pdpShareEventName,
                displayName: "PDP Share Event Name",
                editorTemplate: "TextField",
                defaultValue: "pdp_share",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.pdpShareEvent,
            },
            {
                name: fields.pdpPrintEvent,
                displayName: "PDP Print Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.pdpPrintEventName,
                displayName: "PDP Print Event Name",
                editorTemplate: "TextField",
                defaultValue: "pdp_print",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.pdpPrintEvent,
            },
            {
                name: fields.pdpDocumentClickEvent,
                displayName: "PDP Document Click Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.pdpDocumentClickEventName,
                displayName: "PDP Document Click Event Name",
                editorTemplate: "TextField",
                defaultValue: "pdp_doc_click",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.pdpDocumentClickEvent,
            },
            {
                name: fields.pdpBannerClickEvent,
                displayName: "PDP Banner Click Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.pdpBannerClickEventName,
                displayName: "PDP Banner Click Event Name",
                editorTemplate: "TextField",
                defaultValue: "pdp_banner_click",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.pdpBannerClickEvent,
            },
            {
                name: fields.fileDownloadEvent,
                displayName: "File Download Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.fileDownloadEventName,
                displayName: "File Download Event Name",
                editorTemplate: "TextField",
                defaultValue: "file_download",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.fileDownloadEvent,
            },
            {
                name: fields.uploadBulkOrderEvent,
                displayName: "Upload Bulk Order Event ",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.uploadBulkOrderEventName,
                displayName: "Upload Bulk Order Event Name",
                editorTemplate: "TextField",
                defaultValue: "upload_bulk_order",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.uploadBulkOrderEvent,
            },
            {
                name: fields.quickBulkOrderEvent,
                displayName: "Quick Bulk Order Event ",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.quickBulkOrderEventName,
                displayName: "Quick Bulk Order Event Name",
                editorTemplate: "TextField",
                defaultValue: "quick_bulk_order",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.quickBulkOrderEvent,
            },
            {
                name: fields.updateZipCodeEvent,
                displayName: "Update Zip Code Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.updateZipCodeEventName,
                displayName: "Update Zip Code Event Name",
                editorTemplate: "TextField",
                defaultValue: "update_zip_code",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.updateZipCodeEvent,
            },
            {
                name: fields.hpRecentOrdersEvent,
                displayName: "Homepage Recent Orders Click Name",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.hpRecentOrdersEventName,
                displayName: "Homepage Recent Orders Click Name",
                editorTemplate: "TextField",
                defaultValue: "hp_recent_orders_click",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.hpRecentOrdersEvent,
            },
            {
                name: fields.hpRecentSavedCartsEvent,
                displayName: "Homepage Recent Orders Click Name",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },

            {
                name: fields.hpRecentSavedCartsEventName,
                displayName: "Homepage Recent Orders Click Name",
                editorTemplate: "TextField",
                defaultValue: "hp_recent_saved_carts",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.hpRecentSavedCartsEvent,
            },

            {
                name: fields.compareProductsEvent,
                displayName: "Compare Products Event ",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },

            {
                name: fields.compareProductsEventName,
                displayName: "Compare Products Event Name",
                editorTemplate: "TextField",
                defaultValue: "compare_products",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.compareProductsEvent,
            },
            {
                name: fields.autoCompleteSearchEvent,
                displayName: "Autocomplete Search Event ",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },

            {
                name: fields.autoCompleteSearchEventName,
                displayName: "Autocomplete Search Event Name",
                editorTemplate: "TextField",
                defaultValue: "autocomplete_search",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.autoCompleteSearchEvent,
            },
        ],
    },
};

export default widgetModule;
